// Reusable hooks for the public site to fetch CMS-managed data from MongoDB.
// All hooks gracefully fall back to the bundled static payload (blogPosts.js,
// blogContent.js, caseStudies.js) so the public site never goes blank even if
// the API is unreachable or empty.
import { useEffect, useState } from "react";
import { fetchBlogPosts, fetchBlogPost } from "@/data/blogApi";
import { fetchCaseStudies, fetchCaseStudy } from "@/data/caseStudiesApi";
import axios from "axios";
import staticPosts from "@/data/blogPosts";
import staticContent from "@/data/blogContent";
import staticCaseStudies from "@/data/caseStudies";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/** Returns { posts, loading } for the full blog list (static fallback baked in). */
export function useBlogPosts() {
  const [posts, setPosts] = useState(null);
  useEffect(() => {
    let mounted = true;
    fetchBlogPosts().then((p) => {
      if (mounted) setPosts(p);
    });
    return () => {
      mounted = false;
    };
  }, []);
  return { posts: posts || [], loading: posts === null };
}

/** Returns { post, loading } for a single post by slug. */
export function useBlogPost(slug) {
  const [post, setPost] = useState(null);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    let mounted = true;
    setLoading(true);
    fetchBlogPost(slug).then((p) => {
      if (!mounted) return;
      setPost(p);
      setLoading(false);
    });
    return () => {
      mounted = false;
    };
  }, [slug]);
  return { post, loading };
}

/** Returns { caseStudies, loading }. */
export function useCaseStudies() {
  const [items, setItems] = useState(null);
  useEffect(() => {
    let mounted = true;
    fetchCaseStudies().then((c) => {
      if (mounted) setItems(c);
    });
    return () => {
      mounted = false;
    };
  }, []);
  return { caseStudies: items || [], loading: items === null };
}

/** Returns { caseStudy, loading } for a single case study by slug. */
export function useCaseStudy(slug) {
  const [item, setItem] = useState(null);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    let mounted = true;
    setLoading(true);
    fetchCaseStudy(slug).then((c) => {
      if (!mounted) return;
      setItem(c);
      setLoading(false);
    });
    return () => {
      mounted = false;
    };
  }, [slug]);
  return { caseStudy: item, loading };
}

// ---- Site settings ----
let _settingsCache = null;
let _settingsPromise = null;

async function fetchSiteSettings() {
  if (_settingsCache) return _settingsCache;
  if (_settingsPromise) return _settingsPromise;
  _settingsPromise = (async () => {
    try {
      const { data } = await axios.get(`${API}/site-settings`, { timeout: 8000 });
      if (data) {
        _settingsCache = data;
        return data;
      }
    } catch {
      // ignore - return skeleton
    }
    _settingsCache = {
      homepage: {},
      suppliers: [],
      testimonials: [],
      social_links: {},
      contact_info: {},
    };
    return _settingsCache;
  })();
  return _settingsPromise;
}

/** Returns { settings, loading } - single global site settings doc. */
export function useSiteSettings() {
  const [settings, setSettings] = useState(_settingsCache);
  useEffect(() => {
    if (_settingsCache) return;
    let mounted = true;
    fetchSiteSettings().then((s) => {
      if (mounted) setSettings(s);
    });
    return () => {
      mounted = false;
    };
  }, []);
  return { settings: settings || {}, loading: settings === null };
}

// Re-exported for places that need raw static fallback
export { staticPosts, staticContent, staticCaseStudies };
